import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import type { ButtonVariantProp } from "~/lib/ui/components/ui/Button";
import { Button } from "~/lib/ui/components/ui/Button";
import { cn } from "~/lib/ui/utils/utils";

const DialogRoot = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) => (
  <DialogPrimitive.Overlay
    className={cn(
      `
        fixed inset-0 z-50 overflow-hidden bg-black/60 backdrop-blur-sm

        data-[state=closed]:animate-out data-[state=closed]:fade-out-0

        data-[state=open]:animate-in data-[state=open]:fade-in-0
      `,
      className,
    )}
    {...props}
  />
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * Interactions with the demo tour's narrator card, which floats above every
 * dialog (z-60 vs z-50), must not dismiss the dialog under it: the tour
 * deep-links dialogs open and its Next/Finish buttons sit outside them.
 */
function isDemoTourInteraction(event: {
  detail: { originalEvent: Event };
}): boolean {
  const target = event.detail.originalEvent.target;
  return (
    target instanceof Element &&
    target.closest("[data-demo-tour-card]") !== null
  );
}

const DialogContent = ({
  children,
  className,
  forceFocus,
  fullScreen = false,
  hideCloseButton = false,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  forceFocus?: boolean;
  /**
   * Fill the viewport edge to edge instead of centring a floating panel. For
   * dense workbench surfaces (large tables, side-by-side panes) where the
   * default `sm:max-w-lg` card is the wrong container.
   */
  fullScreen?: boolean;
  /**
   * Skip the built-in floating close control, for content that renders its
   * own close affordance (e.g. inside a header bar).
   */
  hideCloseButton?: boolean;
}) => (
  <DialogPortal>
    <DialogOverlay
      className={cn(`
        fixed inset-0 flex flex-col items-center

        sm:overflow-hidden
      `)}
    >
      <div
        className={cn(
          "flex h-full w-full flex-col",
          !fullScreen && "sm:h-auto sm:min-h-fit sm:w-auto sm:justify-center",
        )}
      >
        <DialogPrimitive.Content
          className={cn(
            `
              relative z-50 flex h-full max-h-dvh w-full flex-col overflow-y-auto
              bg-background shadow-lg duration-200

              data-[state=closed]:animate-out data-[state=closed]:fade-out-0

              data-[state=open]:animate-in data-[state=open]:duration-300
              data-[state=open]:ease-[cubic-bezier(0.2,0.7,0.2,1)]
              data-[state=open]:fade-in-0
              data-[state=open]:slide-in-from-bottom-[26px]
            `,
            fullScreen
              ? "sm:max-w-none"
              : `
                border border-subtle

                sm:mb-48 sm:mt-24 sm:h-auto sm:max-h-[calc(100vh-12rem)]
                sm:max-w-lg sm:overflow-visible sm:rounded-xl
              `,
            className,
          )}
          onEscapeKeyDown={
            forceFocus
              ? (e) => {
                  e.preventDefault();
                }
              : undefined
          }
          onInteractOutside={(e) => {
            if (forceFocus || isDemoTourInteraction(e)) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (forceFocus || isDemoTourInteraction(e)) e.preventDefault();
          }}
          {...props}
        >
          {children}
          {!hideCloseButton && (
            <DialogPrimitive.Close
              className={`
                absolute right-4 top-4 flex size-7 items-center justify-center
                rounded-md bg-muted/60 text-muted-foreground
                ring-offset-background transition-colors

                disabled:pointer-events-none

                focus-visible:outline-hidden focus-visible:ring-2
                focus-visible:ring-ring focus-visible:ring-offset-2

                hover:bg-muted hover:text-foreground
              `}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </div>
    </DialogOverlay>
  </DialogPortal>
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      `
        flex flex-col space-y-2 rounded-t-xl border-b border-subtle
        bg-background p-6 text-center

        sm:text-left
      `,
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      `flex flex-row space-x-2 border-t border-subtle p-6`,
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description
    className={cn("text-base text-muted-foreground", className)}
    {...props}
  />
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

type DialogProps = {
  children?: React.ReactNode;
  confirmButtonVariant?: ButtonVariantProp;
  confirmTitle?: string;
  cancelTitle?: string;
  dialogDescription?: React.ReactNode;
  dialogTitle?: React.ReactNode;
  disabled?: boolean;
  leftButton?: React.ReactNode | string;
  loading?: boolean;
  maxWidth?: number;
  modal?: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  trigger?: React.ReactNode | string;
  footer?: React.ReactNode | string;
  hideCloseButton?: boolean;
  hideConfirmButton?: boolean;
  className?: string;
  confirmButton?: React.ReactNode;
  forceFocus?: boolean;
};

function Dialog(props: DialogProps) {
  const {
    cancelTitle = "Cancel",
    children,
    className,
    confirmButton,
    confirmButtonVariant = "default",
    confirmTitle = "Save Changes",
    dialogDescription,
    dialogTitle = "Are you sure?",
    disabled = false,
    footer,
    forceFocus = false,
    hideCloseButton = false,
    hideConfirmButton = false,
    leftButton = undefined,
    loading = false,
    maxWidth,
    modal = true,
    onConfirm,
    onOpenChange,
    open,
    trigger,
  } = props;

  const shouldShowLeftButton = leftButton != null || !hideCloseButton;
  const shouldShowConfirmButton = confirmButton != null || !hideConfirmButton;
  const shouldShowFooter = shouldShowLeftButton || shouldShowConfirmButton;

  return (
    <DialogRoot
      modal={modal}
      onOpenChange={
        forceFocus
          ? (open) => {
              // If forceFocus is true, only allow explicit open changes, not closing
              if (open === true) {
                onOpenChange(open);
              }
            }
          : onOpenChange
      }
      open={open}
    >
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        className={cn(
          `
            max-w-[425px]

            md:w-[700px]
          `,
          className,
        )}
        forceFocus={forceFocus}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
        style={{ maxWidth }}
      >
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          {dialogDescription && (
            <DialogDescription>{dialogDescription}</DialogDescription>
          )}
        </DialogHeader>
        {children != null && (
          <div className={`min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-6`}>
            {children}
          </div>
        )}
        {footer != null && footer}
        {footer == null && shouldShowFooter && (
          <DialogFooter className="flex items-end justify-end gap-2">
            {}
            {leftButton != null ? (
              leftButton
            ) : hideCloseButton ? (
              <div />
            ) : (
              <Button
                disabled={disabled || loading}
                onClick={() => onOpenChange(false)}
                variant="secondary"
              >
                {cancelTitle}
              </Button>
            )}
            {}
            {confirmButton != null ? (
              confirmButton
            ) : hideConfirmButton ? (
              <div />
            ) : (
              <Button
                className="w-fit"
                disabled={disabled || loading}
                onClick={onConfirm}
                variant={confirmButtonVariant}
              >
                {loading ? "Loading..." : confirmTitle}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </DialogRoot>
  );
}

export {
  Dialog,
  DialogRoot,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
